<script>
    import moment from 'moment'
    import { startCase } from 'lodash'
    import { createSharedComposable, useNow } from '@vueuse/core'

    import { DISPOSITION_DOT_CLASSES, DISPOSITION_TEXT_CLASSES, dispositionColor } from '@/Components/Forms/ClientDisposition.vue'

    /**
     * One clock shared by every mounted entry, so relative times refresh each minute without an interval per entry.
     */
    export const useMinuteClock = createSharedComposable(() => useNow({ interval: 60 * 1000 }))

    const CAREGIVER_COFFEE_BASE_URL = import.meta.env.VITE_COFFEE_BASEURL + '/user/recruiter-candidates/personalinfo/id/'

    /**
     * The caregiver's Coffee profile, or null when the event does not carry their Coffee job profile id.
     */
    export function caregiverCoffeeUrl(caregiver) {
        return caregiver?.coffee_job_profile_id ? CAREGIVER_COFFEE_BASE_URL + caregiver.coffee_job_profile_id : null
    }

    /**
     * The headline of a disposition change: the text before the coloured new disposition, e.g. "Disposition set: Lead ·"
     * or "Disposition changed: Prospect · Searching → Client ·". With no previous type or disposition the disposition
     * was set, not changed; a previous value that is only partly known shows just the known part.
     */
    export function dispositionHeadline(disposition) {
        const { old_type = null, old_disposition = null, new_type = null, new_disposition = null } = disposition ?? {}
        const typeLabel = startCase(new_type ?? '')
        const label = startCase(new_disposition ?? '')
        const previous = [old_type, old_disposition].filter(Boolean).map(startCase).join(' · ')

        let prefix = `Disposition changed: ${previous} →`
        if (!previous) {
            prefix = 'Disposition set:'
        } else if (old_type === new_type && old_disposition === new_disposition) {
            prefix = 'Disposition reason updated:'
        }

        return {
            prefix: [prefix, typeLabel && label ? `${typeLabel} ·` : typeLabel].filter(Boolean).join(' '),
            label,
            class: DISPOSITION_TEXT_CLASSES[dispositionColor(new_type, new_disposition) ?? 'black'],
        }
    }

    const AUTHOR_LABELS = {
        note: 'Note by',
        feedback: 'Added by',
    }

    /**
     * Event types that always name a doer in the meta line, falling back to "Unknown".
     */
    const AUTHORED_TYPES = ['note', 'feedback']

    /**
     * Event types whose meta line shows only when they happened, never who did it.
     */
    const AUTHORLESS_TYPES = ['disposition_change']

    /**
     * The edit textarea text: note bodies are stored HTML, feedback comments are plain text.
     */
    export function editableText(event) {
        return event?.type === 'feedback' ? String(event?.text ?? '').trim() : noteTextForEditing(event?.text)
    }

    /**
     * Converts a stored note body (HTML or plain text) into plain text for the edit textarea.
     */
    export function noteTextForEditing(body) {
        const withBreaks = String(body ?? '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/(p|div|li)>/gi, '\n')
        const text = typeof DOMParser === 'undefined' ? withBreaks.replace(/<[^>]*>/g, '') : new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? ''

        return text.replace(/\n{3,}/g, '\n\n').trim()
    }

    /**
     * A run of two or more line breaks (with only whitespace or non-breaking spaces between them) in a stored note body.
     */
    const PARAGRAPH_BREAK_PATTERN = /<br\s*\/?>(?:(?:\s|&nbsp;|&#160;|&#xa0;)*<br\s*\/?>)+/gi

    const PARAGRAPH_GAP_HTML = '<span class="block h-1.5" aria-hidden="true"></span>'

    /**
     * For rendering only: collapses each run of two or more breaks in a note body into one line break with a small
     * gap, so paragraph breaks don't read as entry breaks. Only the break runs are replaced, by a fixed element; a single
     * break and all other content stay untouched.
     */
    export function collapseParagraphBreaks(html) {
        return String(html ?? '').replace(PARAGRAPH_BREAK_PATTERN, PARAGRAPH_GAP_HTML)
    }

    const NOTES_EVENT_HASH_PATTERN = /^#notes-(\d+)$/

    /**
     * The event's time as unix seconds, the id in its `#notes-<unix seconds>` permalink; null without a readable time.
     */
    export function notesEventTimestamp(event) {
        if (!event?.occurred_at) {
            return null
        }
        const occurredAt = moment(event.occurred_at)

        return occurredAt.isValid() ? occurredAt.unix() : null
    }

    /**
     * The entry's permalink on the client profile (`profileUrl`): `<profileUrl>#notes-<unix seconds>`, or null when the
     * profile or the event time is unknown.
     */
    export function notesEventPermalink(profileUrl, event) {
        const timestamp = notesEventTimestamp(event)

        return profileUrl && timestamp !== null ? `${profileUrl}#notes-${timestamp}` : null
    }

    /**
     * The event a `#notes-<unix seconds>` hash points at, or null for any other hash or no match. Times are to the
     * second, so events in the same second share a link and the first in timeline order (newest first) wins.
     */
    export function eventForNotesHash(events, hash) {
        const match = String(hash ?? '').match(NOTES_EVENT_HASH_PATTERN)
        if (!match) {
            return null
        }
        const timestamp = Number(match[1])

        return (events ?? []).find((event) => notesEventTimestamp(event) === timestamp) ?? null
    }

    /**
     * One line of plain text describing an event, for compact lists such as the mobile recent activity.
     */
    export function eventPreview(event) {
        switch (event?.type) {
            case 'note':
                return noteTextForEditing(event.text)
            case 'feedback': {
                const title = event.counterpart?.name ? `Feedback for caregiver ${event.counterpart.name}` : 'Feedback'
                const text = String(event.text ?? '').trim()

                return text ? `${title}: ${text}` : title
            }
            case 'disposition_change': {
                const headline = dispositionHeadline(event.data)

                return [headline.prefix, headline.label].filter(Boolean).join(' ')
            }
            case 'caregiver_start':
                return `Caregiver ${event.counterpart?.name ?? 'Unknown'} started`
            default:
                return startCase(event?.type ?? '')
        }
    }
</script>

<script setup>
    import { computed, onUnmounted, ref, watch } from 'vue'
    import { useTextareaAutosize } from '@vueuse/core'

    import { IconDelete16Regular, IconEdit16Regular } from '@iconify-prerendered/vue-fluent'

    import LatteRatingCircles, { FEEDBACK_RATINGS, RATING_CIRCLE_BG_CLASSES, RATING_TEXT_CLASSES } from '@/Components/Feedback/LatteRatingCircles.vue'
    import { useNotificationStore } from '@/Stores'

    const props = defineProps({
        event: {
            type: Object,
            required: true,
        },
        highlighted: {
            type: Boolean,
            default: false,
        },
        isFirst: {
            type: Boolean,
            default: false,
        },
        isLast: {
            type: Boolean,
            default: false,
        },
        deleting: {
            type: Boolean,
            default: false,
        },
        editing: {
            type: Boolean,
            default: false,
        },
        saving: {
            type: Boolean,
            default: false,
        },
        /**
         * The client's Latte profile URL (origin and path) that entry permalinks point at; without it the time is not a
         * link.
         */
        profileUrl: {
            type: String,
            default: null,
        },
    })

    const emit = defineEmits(['delete', 'edit', 'cancel-edit', 'save'])

    /**
     * Every entry's dot is the same size and sits centred on the first text line; notes and other neutral entries share
     * the composer's grey note circle.
     */
    const DOT_SIZE_CLASS = 'size-2.5'

    const NEUTRAL_DOT_CLASS = RATING_CIRCLE_BG_CLASSES.none

    const DOT_CLASSES = {
        caregiver_start: 'bg-lifeworx-blue-600',
    }

    const CAREGIVER_LINK_CLASS = 'font-semibold text-lifeworx-blue-800 underline hover:no-underline'

    const CAREGIVER_NAME_CLASS = 'font-semibold text-lifeworx-blue-800'

    /**
     * The connector runs from the first dot to the last through every gap: entries after the first start it at their
     * top edge, entries before the last carry it across the list gap (gap-5) to the next entry, and the first and last
     * stop it at their dot's centre (the entry's py-2 plus half the h-5 dot row).
     */
    const connectorClass = computed(() => [props.isFirst ? 'top-[18px]' : 'top-0', props.isLast ? 'h-[18px]' : '-bottom-5'])

    const authorName = computed(() => props.event.author?.name ?? 'Unknown')

    const hasAuthor = computed(() => !AUTHORLESS_TYPES.includes(props.event.type) && (Boolean(props.event.author) || AUTHORED_TYPES.includes(props.event.type)))

    const noteHtml = computed(() => collapseParagraphBreaks((props.event.text ?? '').replace(/\n/g, '<br>')))

    const now = useMinuteClock()

    /**
     * Relative to the shared clock, which can trail a just-added event by up to a minute: an event "after" the clock
     * reads as just now rather than in the future.
     */
    const relativeDate = computed(() => {
        if (!props.event.occurred_at) {
            return ''
        }
        const occurredAt = moment(props.event.occurred_at)

        return occurredAt.from(moment.max(moment(now.value), occurredAt))
    })

    const absoluteDate = computed(() => (props.event.occurred_at ? moment(props.event.occurred_at).format('LLL') : ''))

    const TIME_LINK_CLASS = 'cursor-pointer rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lifeworx-blue-400'

    const permalink = computed(() => notesEventPermalink(props.profileUrl, props.event))

    const timeTitle = computed(() => (permalink.value ? `Click to Copy Link - ${absoluteDate.value}` : absoluteDate.value))

    /**
     * Copies the entry's permalink, notifying like the contact link copy does.
     */
    async function copyPermalink() {
        if (!permalink.value) {
            return
        }
        const notificationStore = useNotificationStore()
        try {
            await navigator.clipboard.writeText(permalink.value)
            notificationStore.add('Notes & Activity timeline entry link copied')
        } catch (e) {
            notificationStore.add('Failed to Copy Link', 'error')
        }
    }

    const dotClass = computed(() => {
        if (props.event.type === 'feedback') {
            return [DOT_SIZE_CLASS, FEEDBACK_RATINGS.includes(props.event.data?.rating) ? RATING_CIRCLE_BG_CLASSES[props.event.data?.rating] : NEUTRAL_DOT_CLASS]
        }
        if (props.event.type === 'disposition_change') {
            const { new_type = null, new_disposition = null } = props.event.data ?? {}

            return [DOT_SIZE_CLASS, DISPOSITION_DOT_CLASSES[dispositionColor(new_type, new_disposition)] ?? NEUTRAL_DOT_CLASS]
        }

        return [DOT_SIZE_CLASS, DOT_CLASSES[props.event.type] ?? NEUTRAL_DOT_CLASS]
    })

    const headline = computed(() => dispositionHeadline(props.event.data))

    const feedbackWordClass = computed(() => RATING_TEXT_CLASSES[props.event.data?.rating] ?? '')

    /**
     * Caregiver starts have no author or actions, so their time sits on the text line instead of a meta line.
     */
    const hasMetaRow = computed(() => props.event.type !== 'caregiver_start')

    const caregiverUrl = computed(() => caregiverCoffeeUrl(props.event.counterpart))

    const reasons = computed(() => (props.event.data?.reason ?? []).filter(Boolean).map(startCase))

    const changeExpired = ref(false)

    /**
     * The server sends can_edit and can_delete from the same author window; the expiry timer hides the edit action once
     * changeable_until passes. Delete is offered inside the editor.
     */
    const showChangeActions = computed(() => !changeExpired.value && !props.editing && Boolean(props.event.can_edit))

    const isFeedback = computed(() => props.event.type === 'feedback')

    const changeNoun = computed(() => (isFeedback.value ? 'feedback' : 'note'))

    const draftText = ref('')
    const draftTextarea = ref(null)

    useTextareaAutosize({ element: draftTextarea, input: draftText })

    const draftRating = ref(null)

    const canSaveDraft = computed(() => draftText.value.trim() !== '' && !props.saving && (!isFeedback.value || FEEDBACK_RATINGS.includes(draftRating.value)))

    let expiryTimeout = null

    function scheduleChangeExpiry() {
        clearTimeout(expiryTimeout)
        changeExpired.value = false
        if (!props.event.can_edit || !props.event.changeable_until) {
            return
        }
        const remaining = moment(props.event.changeable_until).diff(moment())
        if (remaining <= 0) {
            changeExpired.value = true
            return
        }
        expiryTimeout = setTimeout(() => {
            changeExpired.value = true
        }, remaining)
    }

    function saveDraft() {
        if (!canSaveDraft.value) {
            return
        }
        emit('save', { event: props.event, text: draftText.value, rating: isFeedback.value ? draftRating.value : null })
    }

    watch(() => [props.event.can_edit, props.event.changeable_until], scheduleChangeExpiry, { immediate: true })

    watch(
        () => props.editing,
        (editing) => {
            if (editing) {
                draftText.value = editableText(props.event)
                draftRating.value = props.event.data?.rating ?? null
            }
        },
        { immediate: true },
    )

    onUnmounted(() => {
        clearTimeout(expiryTimeout)
    })
</script>

<template>
    <li
        :data-event-key="event.key"
        class="relative flex gap-3 rounded-md px-2 py-2 transition-colors duration-700"
        :class="highlighted ? 'bg-lifeworx-yellow-50' : 'bg-transparent'"
    >
        <span
            v-if="!(isFirst && isLast)"
            data-test="timeline-connector"
            class="absolute left-4 w-px -translate-x-1/2 bg-zinc-200/60"
            :class="connectorClass"
            aria-hidden="true"
        />
        <div class="relative flex h-5 w-4 shrink-0 items-center justify-center">
            <span
                data-test="timeline-dot"
                class="shrink-0 rounded-full"
                :class="dotClass"
                aria-hidden="true"
            />
        </div>

        <div class="min-w-0 grow text-sm text-lifeworx-blue-800">
            <template v-if="event.type === 'note'">
                <div
                    v-if="!editing"
                    data-test="note-body"
                    class="break-words pr-6"
                    v-html="noteHtml"
                />
            </template>

            <template v-else-if="event.type === 'feedback'">
                <div
                    data-test="feedback-title"
                    class="text-zinc-500"
                >
                    <span
                        data-test="feedback-word"
                        class="font-semibold"
                        :class="feedbackWordClass"
                        >Feedback</span
                    >
                    <template v-if="event.counterpart?.name">
                        for caregiver
                        <a
                            v-if="caregiverUrl"
                            data-test="feedback-caregiver-link"
                            :href="caregiverUrl"
                            target="_blank"
                            rel="noopener"
                            :class="CAREGIVER_LINK_CLASS"
                            >{{ event.counterpart.name }}</a
                        >
                        <span
                            v-else
                            :class="CAREGIVER_NAME_CLASS"
                            >{{ event.counterpart.name }}</span
                        >
                    </template>
                </div>
                <div
                    v-if="event.text && !editing"
                    data-test="feedback-text"
                    class="whitespace-pre-line break-words pr-6"
                >
                    {{ event.text }}
                </div>
            </template>

            <template v-else-if="event.type === 'disposition_change'">
                <div data-test="disposition-summary">
                    {{ headline.prefix }}
                    <span
                        class="font-bold"
                        :class="headline.class"
                        v-text="headline.label"
                    />
                </div>
                <div
                    v-if="reasons.length > 0"
                    data-test="disposition-reason"
                >
                    Reason: {{ reasons.join(', ') }}
                </div>
            </template>

            <div
                v-else-if="event.type === 'caregiver_start'"
                data-test="caregiver-start-row"
                class="flex items-end gap-2"
            >
                <div
                    data-test="caregiver-start"
                    class="min-w-0 grow"
                >
                    Caregiver
                    <a
                        v-if="caregiverUrl"
                        data-test="caregiver-start-link"
                        :href="caregiverUrl"
                        target="_blank"
                        rel="noopener"
                        :class="CAREGIVER_LINK_CLASS"
                        >{{ event.counterpart.name ?? 'Unknown' }}</a
                    >
                    <span
                        v-else
                        :class="CAREGIVER_NAME_CLASS"
                        >{{ event.counterpart?.name ?? 'Unknown' }}</span
                    >
                    started
                </div>
                <component
                    :is="permalink ? 'button' : 'span'"
                    data-test="event-time"
                    :type="permalink ? 'button' : undefined"
                    class="shrink-0 whitespace-nowrap text-xs leading-5 text-zinc-400"
                    :class="permalink && TIME_LINK_CLASS"
                    :title="timeTitle"
                    @click="copyPermalink"
                >
                    {{ relativeDate }}
                </component>
            </div>

            <div v-else>
                {{ startCase(event.type ?? '') }}
            </div>

            <div
                v-if="editing"
                class="flex flex-col gap-2"
            >
                <textarea
                    ref="draftTextarea"
                    v-model="draftText"
                    data-test="edit-note"
                    rows="3"
                    class="min-h-20 w-full resize-none overflow-hidden rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400"
                    :disabled="saving"
                    @keydown.meta.enter.prevent="saveDraft"
                    @keydown.ctrl.enter.prevent="saveDraft"
                    @keydown.esc.prevent="emit('cancel-edit', event)"
                />
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        data-test="delete-note"
                        class="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-lifeworx-red-500 transition-colors hover:bg-lifeworx-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        :title="`Delete ${changeNoun}`"
                        :disabled="deleting || saving"
                        @click="emit('delete', event)"
                    >
                        <IconDelete16Regular
                            class="size-4"
                            aria-hidden="true"
                        />
                        Delete
                    </button>
                    <LatteRatingCircles
                        v-if="isFeedback"
                        v-model="draftRating"
                        data-test="edit-feedback-rating"
                        :ratings="FEEDBACK_RATINGS"
                        :disabled="saving"
                    />
                    <div class="ml-auto flex gap-2">
                        <button
                            type="button"
                            data-test="cancel-edit-note"
                            class="rounded-md px-3 py-2 text-sm font-semibold text-zinc-600 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="saving"
                            @click="emit('cancel-edit', event)"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            data-test="save-edit-note"
                            class="rounded-md bg-lifeworx-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lifeworx-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="!canSaveDraft"
                            @click="saveDraft"
                        >
                            {{ saving ? 'Saving…' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>

            <div
                v-if="hasMetaRow"
                data-test="event-meta"
                class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500"
            >
                <span v-if="hasAuthor">
                    {{ AUTHOR_LABELS[event.type] ?? 'By' }} <span class="font-semibold text-lifeworx-blue-600">{{ authorName }}</span>
                </span>
                <button
                    v-if="showChangeActions"
                    type="button"
                    data-test="edit-note-action"
                    class="inline-flex items-center gap-0.5 rounded px-1 text-zinc-400 transition-colors hover:bg-lifeworx-blue-50 hover:text-lifeworx-blue-600 disabled:opacity-50"
                    :title="`Edit ${changeNoun}`"
                    :disabled="deleting"
                    @click="emit('edit', event)"
                >
                    <IconEdit16Regular
                        class="size-3.5"
                        aria-hidden="true"
                    />
                    <span>Edit</span>
                </button>
                <div
                    data-test="event-meta-end"
                    class="ml-auto flex shrink-0 items-center gap-2"
                >
                    <component
                        :is="permalink ? 'button' : 'span'"
                        data-test="event-time"
                        :type="permalink ? 'button' : undefined"
                        class="whitespace-nowrap text-zinc-400"
                        :class="permalink && TIME_LINK_CLASS"
                        :title="timeTitle"
                        @click="copyPermalink"
                    >
                        {{ relativeDate }}
                    </component>
                </div>
            </div>
        </div>
    </li>
</template>
